import { FrontendRoutes } from '@helpers/Constants/Navigation';
import { CustomerOrderService } from './CustomerService';
import {
  AddonsListProps,
  NewOrderInitiationProps,
  MasterPackage,
  MasterProduct,
  OrderServiceProps,
  PackageMaster,
  ProductMasterProps,
  ProductCustomization,
  DetailsViewStateProps
} from '@helpers/Models';
import {
  ProductType,
  CUSTOMER_DETAILS,
  CUSTOMER_ID,
  CURRENT_CUSTOMIZING_ID,
} from '@helpers/Constants';
import { formatDateUSLong } from '@helpers/DateTime';

/**
 * Initializes a new order for a selected customer.
 * @param customerId - The unique ID of the customer for whom the order is being created.
 * @param navigate - A function (typically from `useNavigate`) used to programmatically navigate between routes.
 * @param customerData - A subset of customer details required for order creation, saved to `localStorage` under the key `customerDetails`.
 *
 * @returns void
 *
 */
export const handleNewOrderInitiation = (
  navigate: (path: string) => void,
  customerData: NewOrderInitiationProps,
): void => {
  CustomerOrderService.setCustomerId(customerData.id);

  localStorage.setItem(CUSTOMER_DETAILS, JSON.stringify(customerData));
  localStorage.setItem(CUSTOMER_ID, JSON.stringify(customerData.id));
  localStorage.removeItem(CURRENT_CUSTOMIZING_ID);

  navigate(FrontendRoutes.CREATE_ORDER);
};

/**
 * Calculates the total price of a product including quantity, add-ons, and packaging.
 *
 * @param {number} quantity - The number of units of the product being ordered.
 * @param {number[]} addOnIds - Array of add-on IDs selected for the product.
 * @param {Array<{ productPackageTypeId: number, packingPercentage: number }>} packaging - Array of packaging objects, each containing the package type ID and the percentage of product weight to apply packaging cost.
 * @param {ProductMasterProps} master - The master product object containing details such as price, weight, type, and flags.
 * @param {AddonsListProps[]} addonsMaster - Array of available add-ons with their respective prices and IDs.
 * @param {PackageMaster[]} packageMasters - Array of available package types with their respective prices and IDs.
 * @param {number} [orderStatus] - Optional order status; if >= 3, the product price is not multiplied by quantity.
 *
 * @returns {number} - The total price of the product including add-ons and packaging.
 *
 * @description
 * - If `orderStatus >= 3`, the master product price is used directly, otherwise it's multiplied by `quantity`.
 * - Add-ons are calculated based on `quantity` and product `weight`. If `master.upchargeIngredientsFlag` is 0, add-ons are ignored.
 * - Packaging cost is calculated based on the product weight, the `packingPercentage` of each package, and the package price.
 */
export const calculateTotalPrice = (
  quantity: number,
  addOnIds: number[],
  packaging: { productPackageTypeId: number; packingPercentage: number }[],
  master: ProductMasterProps,
  addonsMaster: AddonsListProps[],
  packageMasters: PackageMaster[],
  orderStatus?: number,
): number => {
  let masterProductPrice = 0;
  // If order is invoiced, use the calculated price. Otherwise multiply quantity with price
  if (orderStatus != null && orderStatus >= 3) {
    masterProductPrice = master.price;
  } else {
    masterProductPrice = quantity * master.price;
  }

  const weight = master.productType == ProductType.QUANTITY ? master.weight : 1;

  // Sum of Add-ons
  let addonsTotal =
    addOnIds?.reduce((sum, addOnId) => {
      const addon = addonsMaster.find((a: { id: number }) => a.id === addOnId);
      if (!addon) return sum;
      return sum + addon.price * (quantity * weight);
    }, 0) ?? 0;

  // If upchargeIngredientsFlag is 0, addons price are 0
  if (master.upchargeIngredientsFlag === 0) {
    addonsTotal = 0;
  }

  // Sum of Packaging
  const packagingTotal =
    packaging?.reduce((sum, pack) => {
      const matchedPackage = packageMasters.find(
        (masterPack: { id: number }) =>
          masterPack.id === pack.productPackageTypeId,
      );
      const totalProductWeight = quantity * weight;
      const percentageToAdd = pack.packingPercentage / 100;
      const weightPercentage = totalProductWeight * percentageToAdd;
      const packagingPrice = (matchedPackage?.price ?? 0) * weightPercentage;

      return sum + packagingPrice;
    }, 0) ?? 0;

  return masterProductPrice + addonsTotal + packagingTotal;
};

/**
 * Filters a list of items based on a search keyString string and a specific key.
 *
 * This is a generic utility function that works with arrays of objects
 * (such as `OrderServiceProps`, `MasterProduct`, or `MasterPackage`).
 * It returns only those items where the value at the specified key
 * includes the keyString string (case-insensitive).
 *
 * @template T - A type extending `OrderServiceProps`, `MasterProduct`, or `MasterPackage`.
 * @param {T[]} list - The array of items to search/filter.
 * @param {string} keyString - The search string to match against.
 * @param {keyof T} key - The key within the objects whose value is compared to the keyString.
 * @returns {T[]} A filtered array containing items that match the keyString.
 *
 */
export const searchService = <
  T extends OrderServiceProps | MasterProduct | MasterPackage,
>(
  list: T[],
  keyString: string,
  key: keyof T,
): T[] => {
  if (!keyString.trim()) return list;

  const lowerkeyString = keyString.toLowerCase();
  return list.filter((item) =>
    String(item[key]).toLowerCase().includes(lowerkeyString),
  );
};

/**
 * Formats a date string into a human-readable format "MM / DD / YYYY".
 *
 * @param dateString - The date string to be formatted.
 * @returns A formatted date string in the "MM / DD / YYYY" format.
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return `${date.getMonth() + 1} / ${date.getDate()} / ${date.getFullYear()}`;
};

/**
 * Formats a 10-digit phone number string into a human-readable format.
 *
 * @param {string} phone - A raw 10-digit phone number string (e.g., "1234567890").
 * @returns {string} The formatted phone number in the form "123 456-7890".
 *
 */
export const getFormattedPhoneNumber = (phone: string): string => {
  const areaCode = phone.slice(0, 3);
  const localNumber1 = phone.slice(3, 6);
  const localNumber2 = phone.slice(6);

  return `${areaCode} ${localNumber1}-${localNumber2}`;
};

/**
 * Navigates the user to the product processing task list page for a given order.
 *
 * @param {number} orderId - The ID of the order for which processing should begin.
 * @param {(path: string, options?: { state?: { orderId: number } }) => void} navigate -
 *        A navigation function (typically from React Router) used to change the route.
 *
 * @returns {void} This function performs a side-effect (navigation) and does not return a value.
 *
 */
export const handleProductProcessing = (
  orderId: number,
  navigate: (path: string, options?: { state?: { orderId: number } }) => void,
): void => {
  navigate(FrontendRoutes.PRODUCT_PROCESSING_TASK_LIST, {
    state: { orderId },
  });
};

/**
 * Resets the order data for the current customer by removing their entry
 * from the stored order data in localStorage.
 *
 * - Retrieves the current `customerId` from localStorage (defaults to `0` if not found).
 * - Parses the existing order data via `CustomerOrderService`.
 * - Deletes the order data entry associated with the current customer.
 * - Persists the updated order data back into localStorage using `CustomerOrderService`.
 *
 * @function resetCustomerOrderData
 * @returns {void} This function does not return anything.
 */
export const resetCustomerOrderData = (): void => {
  const customerId = localStorage.getItem(CUSTOMER_ID);
  const parsedOrderData = CustomerOrderService.getParsedOrderData();
  const currentCustomerId = customerId ?? 0;
  delete parsedOrderData[currentCustomerId];
  CustomerOrderService.setParsedOrderData(parsedOrderData);
};

/**
 * Calculates the total price for each selected add-on based on quantity and product weight.
 *
 * @param {number[]} addOnIds - Array of selected add-on IDs.
 * @param {AddonsListProps[]} addonsMaster - Array of available add-ons with their prices and IDs.
 * @param {number} quantity - Quantity of the main product.
 * @param {ProductMasterProps} master - The master product object containing details such as weight, type, and upcharge flag.
 *
 * @returns {Array<{ id: number, name: string, unitPrice: number, total: number } | null>}
 * An array of objects for each add-on containing:
 *   - `id`: The add-on ID
 *   - `name`: The add-on name
 *   - `unitPrice`: Price per unit of the add-on
 *   - `total`: Total price for this add-on (0 if `upchargeIngredientsFlag` is 0)
 *   Returns `null` for add-on IDs that are not found in `addonsMaster`.
 *
 * @description
 * - If the product type is `ProductType.QUANTITY`, the weight of the product is used in calculating add-on totals; otherwise, weight is 1.
 * - The total add-on price is multiplied by `quantity` and `weight`.
 * - If `master.upchargeIngredientsFlag` is 0, all add-on totals are considered 0.
 */
export const getSumOfAddOns = (
  addOnIds: number[],
  addonsMaster: AddonsListProps[],
  quantity: number,
  master: ProductMasterProps,
): Array<{
  id: number;
  name: string;
  unitPrice: number;
  total: number;
} | null> => {
  const weight =
    master.productType === ProductType.QUANTITY ? master.weight : 1;

  return addOnIds.map((addOnId) => {
    const addon = addonsMaster.find((a) => a.id === addOnId);
    if (!addon) return null;
    return {
      id: addon.id,
      name: addon.name,
      unitPrice: addon.price,
      total:
        master.upchargeIngredientsFlag === 0
          ? 0
          : addon.price * quantity * weight,
    };
  });
};

/**
 * Calculates the total price for each selected packaging option based on quantity and product weight.
 *
 * @param {number} quantity - The quantity of the main product.
 * @param {Array<{ productPackageTypeId: number, packingPercentage: number }>} packaging - Array of selected packaging options with package type ID and packing percentage.
 * @param {PackageMaster[]} packageMasters - Array of available package types with their prices, sizes, and names.
 * @param {ProductMasterProps} master - The master product object containing details such as weight and product type.
 *
 * @returns {Array<{ packageId: number, name: string | undefined, size: string | undefined, percentage: number, unitPrice: number, total: number }>}
 * An array of objects for each packaging option containing:
 *   - `packageId`: The package type ID
 *   - `name`: Name of the package type (undefined if not found)
 *   - `size`: Size of the package (undefined if not found)
 *   - `percentage`: Packing percentage applied to the product weight
 *   - `unitPrice`: Price per unit of the package (0 if not found)
 *   - `total`: Total price for this packaging option
 *
 * @description
 * - If the product type is `ProductType.QUANTITY`, the product weight is used; otherwise, weight is treated as 1.
 * - The total price for each package is calculated as `unitPrice * (quantity * weight * (packingPercentage / 100))`.
 * - Returns a detailed breakdown of all selected packaging options.
 */
export const getSumOfPackage = (
  quantity: number,
  packaging: { productPackageTypeId: number; packingPercentage: number }[],
  packageMasters: PackageMaster[],
  master: ProductMasterProps,
): Array<{
  packageId: number;
  name: string | undefined;
  size: number | undefined;
  percentage: number;
  unitPrice: number;
  total: number;
}> => {
  const weight =
    master.productType === ProductType.QUANTITY ? master.weight : 1;
  const totalProductWeight = quantity * weight;

  return packaging.map((pack) => {
    const matchedPackage = packageMasters.find(
      (masterPack) => masterPack.id === pack.productPackageTypeId,
    );
    const weightPercentage =
      totalProductWeight * (pack.packingPercentage / 100);
    const total = (matchedPackage?.price ?? 0) * weightPercentage;

    return {
      packageId: pack.productPackageTypeId,
      name: matchedPackage?.packageType,
      size: matchedPackage?.packageSize,
      percentage: pack.packingPercentage,
      unitPrice: matchedPackage?.price ?? 0,
      total,
    };
  });
};

/**
 * Validates whether a given product's value (weight or quantity) meets the minimum requirement.
 *
 * This function checks the entered `value` for a specific product against its corresponding
 * minimum allowed value (based on the master product configuration). If the entered value
 * is below the minimum, it triggers an error toast message and returns `false`; otherwise, it returns `true`.
 *
 * @param {MasterProduct[]} masterProducts - The list of master product definitions containing product type, weight, and minimum quantity data.
 * @param {ProductCustomization[]} products - The list of customized product instances being validated.
 * @param {number} index - The index of the product in the `products` array to validate.
 * @param {number} value - The user-entered value (weight or quantity) to validate.
 * @param {(message: string, type: 'success' | 'error') => void} showToastMessage - Function to display a toast notification with a given message and type.
 *
 * @returns {boolean} `true` if the entered value meets or exceeds the minimum requirement, otherwise `false`.
 *
 * @remarks
 * - Determines the validation type based on `productType`:
 *   - If `productType === ProductType.QUANTITY`, compares against `minQuantity`.
 *   - If `productType === ProductType.WEIGHT`, compares against `weight`.
 * - Displays a contextual error message when validation fails.
 */
export const validateMinimumRequiredValue = (
  masterProducts: MasterProduct[],
  products: ProductCustomization[],
  index: number,
  value: number,
  showToastMessage: (message: string, type: 'success' | 'error') => void,
): boolean => {
  // Get the current product
  const targetProduct = products[index];
  if (!targetProduct) return false; // Fallback

  // Get the current product's master details
  const masterProduct = masterProducts.find(
    (masterData) => masterData.id === targetProduct.productId,
  );
  if (!masterProduct) return false; // Fallback

  // Get the minimum value
  // If product type is 1 (weight), get the masterProduct.weight and if type is 2 (quantity), get masterProduct.minQuantity
  // If the product type is weight and the minimum weight is 0 lb,
  // use 1 lb as the default value.
  const minimumValue =
    masterProduct.productType === ProductType.QUANTITY
      ? masterProduct.minQuantity
      : masterProduct.weight === 0
        ? 1
        : masterProduct.weight;

  // If the user entered value is less than the minimum required, show error toast message and return false, else return true
  if (value < minimumValue) {
    showToastMessage(
      `The Minimum ${masterProduct.productType === ProductType.WEIGHT ? 'Weight' : 'Quantity'
      } should be at least ${masterProduct.productType === ProductType.WEIGHT
        ? `${masterProduct.weight === 0 ? 1 : masterProduct.weight} Lbs`
        : masterProduct.minQuantity
      }.`,
      'error',
    );
    return false;
  }
  return true;
};

/**
 * Retrieves the saved deposit amount for a specific service from local storage.
 *
 * This function looks up the current order draft within `editOrderData` for a specific
 * customer and species customization set. It searches through the saved services
 * to find a match for the provided `serviceId` and returns its associated deposit amount.
 *
 * @param {number} serviceId - The unique identifier of the service whose deposit is being retrieved.
 * @param {number} customerId - The unique identifier of the customer the order belongs to.
 * @param {number} currentCustomizingId - The index of the specific species/item set currently being customized.
 *
 * @returns {number} The deposit amount associated with the service, or `0` if the data is not found or unavailable.
 *
 * @remarks
 * - Specifically targets the `editOrderData` key in localStorage, used during the order modification workflow.
 * - Utilizes optional chaining to safely traverse the nested JSON structure (Customer -> Species -> Service).
 * - Defaults to `0` if the localStorage is empty, the customer record is missing, or the service cannot be found.
 */
export const getServiceDeposit = (
  serviceId: number,
  customerId: number,
  currentCustomizingId: number,
): number => {
  const raw = localStorage.getItem('editOrderData');
  if (!raw) return 0;
  const all = JSON.parse(raw);
  const currentSpecies = all[customerId]?.species?.[currentCustomizingId];
  const savedService = currentSpecies?.services?.find(
    (s: { serviceId: number; depositAmount?: number }) =>
      s.serviceId === serviceId,
  );
  return savedService?.depositAmount ?? 0;
};

/**
 * Formats a date string into US written format.
 *
 * - Converts the input date into: "MMMM D, YYYY"
 *   (e.g., "July 31, 2026").
 * - Uses a US timezone-aware formatter.
 *
 * Behavior:
 * - If `date` is undefined or empty, returns '-' as a fallback.
 * - Uses a shared date utility so all pages follow the same format.
 *
 * @param date - The date string to format.
 * @returns Formatted US date string or '-' if no date is provided.
 */
export const handleDateFormat = (date: string | undefined): string => {
  return formatDateUSLong(date);
};

/**
 * Navigates to a specified path and passes the order ID in the navigation state.
 *
 * @param {number} orderId - The ID of the order to view details for.
 * @param {(path: string, options?: { state?: { orderId: number } }) => void} navigate -
 *        A navigation function (typically from React Router) that redirects to a new route.
 * @param {string} path - The path to navigate to (e.g., "/order-details").
 *
 * @returns {void} This function does not return a value.
 */
export const handleDetailsView = (
  orderId: number,
  navigate: (path: string, options?: { state?: DetailsViewStateProps }) => void,
  path: string,
  fromPickup?: boolean,
): void => {
  navigate(path, {
    state: { orderId, fromPickup },
  });
};

/**
 * Formats a date range into a concise, human-readable string.
 *
 * - The start date is formatted as "MMM DD" (e.g., "Jan 15")
 * - The end date is formatted as "MMM DD, YYYY" (e.g., "Feb 15, 2024")
 *
 * Example output:
 * "Jan 15 - Feb 15, 2024"
 *
 * @param {string} start - The start date (ISO string)
 * @param {string} end - The end date (ISO string)
 * @returns {string} Formatted date range string
 */
export const formatDateRange = (start: string, end: string): string => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const startOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  const endOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };

  const startFormatted = startDate.toLocaleDateString('en-US', startOptions);
  const endFormatted = endDate.toLocaleDateString('en-US', endOptions);

  return `${startFormatted} - ${endFormatted}`;
};
